interface LocalBusiness {
    name: string;
    description?: string;
    url: string;
    telephone: string;
    email: string;
    address: {
        streetAddress: string;
        addressLocality: string;
        postalCode: string;
        addressCountry: string;
    };
    geo?: {
        latitude: number;
        longitude: number;
    };
    openingHours?: string[];
    priceRange?: string;
    image?: string;
}

interface BreadcrumbItem {
    name: string;
    url: string;
}

interface UseStructuredDataReturn {
    generateLocalBusiness: (business: LocalBusiness) => Record<string, unknown>;
    generateBreadcrumbList: (items: BreadcrumbItem[]) => Record<string, unknown>;
    generateProduct: (product: {
        name: string;
        description: string;
        image: string;
        price: number;
        currency?: string;
        availability?: boolean;
        brand?: string;
        category?: string;
        url?: string;
    }) => Record<string, unknown>;
    generateService: (service: {
        name: string;
        description: string;
        provider: string;
        serviceType: string;
        areaServed?: string[];
        hasOfferCatalog?: {
            name: string;
            itemListElement: Array<{
                name: string;
                description: string;
            }>;
        };
    }) => Record<string, unknown>;
    generateFAQPage: (questions: Array<{ question: string; answer: string }>) => Record<string, unknown>;
    defaultBusinessData: LocalBusiness;
}

export function useStructuredData(): UseStructuredDataReturn {
    /**
     * Generate LocalBusiness schema
     */
    const generateLocalBusiness = (business: LocalBusiness): Record<string, unknown> => {
        return {
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            '@id': business.url,
            name: business.name,
            description: business.description,
            url: business.url,
            telephone: business.telephone,
            email: business.email,
            address: {
                '@type': 'PostalAddress',
                streetAddress: business.address.streetAddress,
                addressLocality: business.address.addressLocality,
                postalCode: business.address.postalCode,
                addressCountry: business.address.addressCountry,
            },
            ...(business.geo && {
                geo: {
                    '@type': 'GeoCoordinates',
                    latitude: business.geo.latitude,
                    longitude: business.geo.longitude,
                },
            }),
            ...(business.openingHours && {
                openingHoursSpecification: business.openingHours.map(
                    (hours) => ({
                        '@type': 'OpeningHoursSpecification',
                        dayOfWeek: hours,
                    }),
                ),
            }),
            ...(business.priceRange && { priceRange: business.priceRange }),
            ...(business.image && { image: business.image }),
            sameAs: [
                'https://www.facebook.com/bagrmb',
                'https://www.instagram.com/bagrmb',
            ],
        };
    };

    /**
     * Generate BreadcrumbList schema
     */
    const generateBreadcrumbList = (items: BreadcrumbItem[]): Record<string, unknown> => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                item: item.url,
            })),
        };
    };

    /**
     * Generate Product schema for equipment
     */
    const generateProduct = (product: {
        name: string;
        description: string;
        image: string;
        price: number;
        currency?: string;
        availability?: boolean;
        brand?: string;
        category?: string;
        url?: string;
    }): Record<string, unknown> => {
        return {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.image,
            brand: {
                '@type': 'Brand',
                name: product.brand || 'Bobcat',
            },
            category: product.category || 'Construction Equipment',
            url: product.url,
            offers: {
                '@type': 'Offer',
                price: product.price,
                priceCurrency: product.currency || 'CZK',
                availability: product.availability
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/OutOfStock',
                priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0],
                seller: {
                    '@type': 'LocalBusiness',
                    name: 'Bagr Moravské Budějovice',
                },
            },
        };
    };

    /**
     * Generate Service schema
     */
    const generateService = (service: {
        name: string;
        description: string;
        provider: string;
        serviceType: string;
        areaServed?: string[];
        hasOfferCatalog?: {
            name: string;
            itemListElement: Array<{
                name: string;
                description: string;
            }>;
        };
    }): Record<string, unknown> => {
        return {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: service.name,
            description: service.description,
            provider: {
                '@type': 'LocalBusiness',
                name: service.provider,
            },
            serviceType: service.serviceType,
            ...(service.areaServed && {
                areaServed: service.areaServed.map((area) => ({
                    '@type': 'City',
                    name: area,
                })),
            }),
            ...(service.hasOfferCatalog && {
                hasOfferCatalog: {
                    '@type': 'OfferCatalog',
                    name: service.hasOfferCatalog.name,
                    itemListElement:
                        service.hasOfferCatalog.itemListElement.map((item) => ({
                            '@type': 'Offer',
                            itemOffered: {
                                '@type': 'Service',
                                name: item.name,
                                description: item.description,
                            },
                        })),
                },
            }),
        };
    };

    /**
     * Generate FAQPage schema
     */
    const generateFAQPage = (
        questions: Array<{ question: string; answer: string }>,
    ): Record<string, unknown> => {
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: questions.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                },
            })),
        };
    };

    /**
     * Default business data
     */
    const defaultBusinessData: LocalBusiness = {
        name: 'Bagr Moravské Budějovice',
        description:
            'Pronájem stavební techniky - minibagrů Bobcat a Yanmar, dumperů. Spolehlivé stroje, konkurenční ceny, doprava po celé ČR.',
        url: 'https://www.bagrmb.cz',
        telephone: '+420703635683',
        email: 'info@bagrmb.cz',
        address: {
            streetAddress: 'Nerudova 1051',
            addressLocality: 'Moravské Budějovice',
            postalCode: '676 02',
            addressCountry: 'CZ',
        },
        geo: {
            latitude: 49.0522,
            longitude: 15.8086,
        },
        openingHours: ['Mo-Fr 07:00-17:00', 'Sa 08:00-12:00'],
        priceRange: '$$',
        image: 'https://www.bagrmb.cz/images/logo.png',
    };

    return {
        generateLocalBusiness,
        generateBreadcrumbList,
        generateProduct,
        generateService,
        generateFAQPage,
        defaultBusinessData,
    };
}
